/**
 * Shared contract for learner-driven model inputs (CT-INTERACT, chemistry/CLAUDE.md
 * "Interactive calculation standard"). A tool lists its fields once; the panel, validation and
 * fail-closed messages come from here so every tool reads the same way.
 */
export interface ModelField<K extends string = string> {
  key: K;
  label: string;
  unit: string;
  min: number;
  max: number;
  step: number;
}

/** Parses a typed value; an empty field is not a number. */
export function parseModelValue(raw: string): number {
  return raw.trim() === '' ? Number.NaN : Number(raw);
}

/** A specific message for a missing or out-of-range value, or null when the value is valid. */
export function modelFieldError(field: Pick<ModelField, 'label' | 'unit' | 'min' | 'max'>, value: number): string | null {
  // Lower-case only the first letter, so symbols such as "T₁" or "pH" keep their case.
  if (!Number.isFinite(value)) return `Enter a number for ${field.label.charAt(0).toLowerCase()}${field.label.slice(1)}.`;
  if (value < field.min || value > field.max) return `${field.label} must be ${field.min}–${field.max} ${field.unit}.`;
  return null;
}

/** The first error across all fields, for pure calculation modules that must fail closed. */
export function firstModelError<K extends string>(fields: readonly ModelField<K>[], values: Record<K, number>): string | null {
  for (const field of fields) {
    const error = modelFieldError(field, values[field.key]);
    if (error) return error;
  }
  return null;
}

/** One stage of a worked calculation, with the learner's numbers already substituted. */
export interface WorkedStep {
  id: string;
  title: string;
  formula: string;
  working: string;
  status: 'done' | 'current' | 'not-reached';
  /** Short right-hand note, for example "4,180 of 33,400 J" or "needs 2,260 J". */
  note?: string;
}

/**
 * A guided "Try this" task for an Explore view (CT-INTERACT C7, teaching goal). `isMet` is a pure
 * check on the tool's own calculation result, so it can be unit-tested; `why` explains the idea the
 * learner has just shown, in one or two sentences.
 */
export interface ExploreChallenge<R> {
  id: string;
  prompt: string;
  hint?: string;
  isMet: (result: R) => boolean;
  why: string;
}

/** Ids of the challenges the result meets, in list order. A null result meets none. */
export function metChallengeIds<R>(challenges: readonly ExploreChallenge<R>[], result: R | null): string[] {
  if (result === null) return [];
  return challenges.filter((challenge) => challenge.isMet(result)).map((challenge) => challenge.id);
}

/** Australian English number formatting with a fixed maximum number of decimals. */
export function formatModelNumber(value: number, digits = 1): string {
  return value.toLocaleString('en-AU', { maximumFractionDigits: digits });
}
